import React, { useEffect, useState } from 'react';

import DefaultApiLimit from '../../../config/defaultLimit';
import { AvailableReport, RequestReportInterFace } from '../../../interface/reports.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  fetchReports,
  setReportFilterData,
  updateReportFilters,
  fetchListFilters,
  setSortData,
} from '../../../redux/slice/ReportSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ExportButton from '../../../shared-components/export-button/ExportButton';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import reportApi from '../../../utils/api/report';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  header: boolean;
}
/**
 * Reports management page
 */
function ReportManagement() {
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    header: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { reports, limit, page, loading, totalPages, totalResults, filters, filterListLoading } =
    useAppSelector((state) => state.report);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const dispatch = useAppDispatch();
  const { sortBy, sortField } = useAppSelector((state) => state.report);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestReportInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchReports({
          ...data,
        }),
      );
    }
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: 's_no' as keyof AvailableReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: 'Date',
      accessorKey: 'createdAt' as keyof AvailableReport,
      sortAllow: true,
    },
    {
      label: 'Station Name',
      accessorKey: 'chargingStation' as keyof AvailableReport,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Power Consumed',
      accessorKey: 'powerConsumed' as keyof AvailableReport,
      sortAllow: true,
    },
    {
      label: 'Revenue Generated',
      accessorKey: 'revenue' as keyof AvailableReport,
      sortAllow: true,
    },
  ];

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchReports({
        ...queryParameters,
        sortField,
        sortBy,
        page: pageCount,
        limit,
      }),
    );
  };

  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setReportFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    dispatch(
      fetchReports({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchReports({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: limitCount,
      }),
    );
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    reportApi
      .fetchReportListExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Date',
          'Station Id',
          'Station Name',
          'Power Consumed',
          'Revenue Generated',
        ];
        const rows = response.data.results.map((row: AvailableReport) => [
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
          row.chargingStation?._id || '',
          row.chargingStation?.name || '',
          row.powerConsumed || '0',
          row?.revenue ? row?.revenue : '0',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="report-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        // notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          tableHeight="70vh"
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={reports.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default ReportManagement;
